import React, { useState, useEffect, useContext, useCallback } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { CircularProgress, Tooltip, Box } from '@mui/material';

import { UserContext } from 'context/userContext';
import { useNotifications } from 'context/NotificationContext'; // Import useNotifications

import { getActivity } from 'services/activity';
import ArgonBox from 'components/ArgonBox'; // For layout if needed

function RecentActivity() {
  const { userId } = useContext(UserContext);
  const { addNotification } = useNotifications();

  const [isLoading, setIsLoading] = useState(true);
  // const [error, setError] = useState(null); // Replaced by notifications
  const [logs, setLogs] = useState([]);
  // No timeout settings in the dashboard widget

  const fetchActivities = useCallback(
    async (showLoadingNotification = true) => {
      if (!userId) {
        // console.log('User not available for fetching activities.');
        setLogs([]); // Clear logs if no user
        setIsLoading(false);
        return;
      }

      if (showLoadingNotification) {
        addNotification({ title: ['Loading Activities'], details: 'Fetching activity history...', severity: 'info' });
      }
      setIsLoading(true);

      try {
        // Optional: Call deleteActivity if it's meant to be a cleanup before fetching
        // await deleteActivity(userId);
        // console.log('Activity cleanup completed (if applicable).');

        const response = await getActivity(userId);
        // Service typically returns { activities } (see full ActivityHistory usage)
        const activities = response?.activities || response?.data?.activities || [];
        if (response?.error) {
          addNotification({
            title: ['Error Fetching Logs'],
            details: response.msg || 'Could not retrieve activity logs.',
            severity: 'error',
          });
          setLogs([]);
        } else {
          const all = activities;

          // Remove redundant In Progress entries if a final status exists for the same activity
          const finalStatusMap = new Map();
          const makeKey = (log) => {
            if (log.operationBatchId) {
              const productKey = `${log.product_name}#${log.product_version}`;
              return `${log.operationBatchId}-${productKey}`;
            }
            return `${log.operation_type}-${log.org_name}-${log.product_name}-${log.product_version}`;
          };

          all.forEach((log) => {
            if (log.status === 'Success' || log.status === 'Failed') {
              finalStatusMap.set(makeKey(log), log.status);
            }
          });

          const filtered = all.filter((log) => {
            if (log.status === 'In Progress') {
              const key = makeKey(log);
              return !finalStatusMap.has(key);
            }
            return true;
          });

          // Sort newest first and keep max 8
          const sorted = filtered
            .filter((l) => !!l.timestamp)
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
            .slice(0, 8);
          setLogs(sorted);
        }
      } catch (err) {
        console.error('Error fetching activity logs:', err);
        addNotification({
          title: ['Fetch Error'],
          details: 'Failed to load activity history. Please try again.',
          severity: 'error',
        });
        setLogs([]);
      } finally {
        setIsLoading(false);
      }
    },
    [userId, addNotification]
  );

  // No-op (not used)

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const columns = [
    {
      field: 'operation_type',
      headerName: 'Operation',
      flex: 0.8,
      minWidth: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const op = (params?.value || '').toString().toLowerCase();
        const title = params?.value || 'Operation';
        if (op.includes('deploy')) {
          return (
            <Tooltip title={title} placement="top">
              <Box sx={{ color: 'info.main', position: 'relative', top: '4px' }}>
                {/* Upload icon for deploy */}
                <span className="material-icons" style={{ fontSize: 18 }}>
                  upload
                </span>
              </Box>
            </Tooltip>
          );
        }
        if (op.includes('commit')) {
          return (
            <Tooltip title={title} placement="top">
              <Box sx={{ color: 'secondary.main', position: 'relative', top: '4px' }}>
                {/* Download icon for commit */}
                <span className="material-icons" style={{ fontSize: 18 }}>
                  download
                </span>
              </Box>
            </Tooltip>
          );
        }
        return (
          <Tooltip title={title} placement="top">
            <Box component="span">{params?.value}</Box>
          </Tooltip>
        );
      },
    },
    { field: 'product_name', headerName: 'Product', flex: 1.2, minWidth: 180, align: 'left', headerAlign: 'left' },
    {
      field: 'product_version',
      headerName: 'Version',
      flex: 0.6,
      minWidth: 80,
      align: 'center',
      headerAlign: 'center',
    },
    { field: 'org_name', headerName: 'Org', flex: 0.7, minWidth: 80, align: 'center', headerAlign: 'center' },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.6,
      minWidth: 40,
      align: 'center',
      headerAlign: 'center',
      display: 'flex',
      alignItems: 'center',
      renderCell: (params) => {
        const status = params.value;
        if (status === 'Success') {
          return (
            <Tooltip title="Success" placement="top">
              <Box sx={{ color: 'success.main', position: 'relative', top: '4px' }}>
                <CheckCircleIcon fontSize="small" />
              </Box>
            </Tooltip>
          );
        }
        if (status === 'Failed') {
          return (
            <Tooltip title="Failed" placement="top">
              <Box sx={{ color: 'error.main', position: 'relative', top: '4px' }}>
                <CancelIcon fontSize="small" />
              </Box>
            </Tooltip>
          );
        }
        if (status === 'In Progress') {
          return (
            <Tooltip title="In Progress" placement="top">
              <Box sx={{ position: 'relative', top: '4px' }}>
                <CircularProgress size={12} sx={{ color: 'info.main' }} />
              </Box>
            </Tooltip>
          );
        }
        return <Box component="span">-</Box>;
      },
    },
    {
      field: 'timestamp',
      headerName: 'Date',
      flex: 1,
      minWidth: 170,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const value = params?.row?.timestamp ?? params.value;
        const date = value ? new Date(value) : null;
        if (!date || isNaN(date.getTime())) {
          // eslint-disable-next-line no-console
          console.log('[Dashboard RecentActivity] Invalid timestamp value:', value, 'row:', params?.row);
          return '-';
        }
        return date.toLocaleString();
      },
    },
  ];

  const rows = logs.map((log, index) => ({
    id: log._id || `log-${index}`,
    operation_type: log.operation_type,
    product_name: log.product_name,
    product_version: log.product_version,
    org_name: log.org_name,
    status: log.status,
    timestamp: log.timestamp,
  }));

  return (
    <ArgonBox py={1}>
      <div style={{ width: '100%' }}>
        <DataGrid
          autoHeight
          rows={rows}
          columns={columns}
          hideFooter
          disableColumnMenu
          disableSelectionOnClick
          sortingOrder={['desc', 'asc']}
          initialState={{ sorting: { sortModel: [{ field: 'timestamp', sort: 'desc' }] } }}
          sx={{
            display: 'flex',
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: (theme) => theme.palette.grey[100],
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            },
            '& .MuiDataGrid-columnHeaderTitleContainer': {
              justifyContent: 'center',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 'medium',
              fontSize: '0.8rem',
            },
            '& .MuiDataGrid-cell': {
              fontSize: '0.8rem',
              borderBottom: (theme) => `1px solid ${theme.palette.grey[200]}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
          }}
          loading={isLoading && rows.length === 0}
        />
      </div>
    </ArgonBox>
  );
}
export default RecentActivity;
